import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import type {
  ActiveMetric,
  LeaderboardEntry,
  LeaderboardResponse,
  LeaderboardSeason,
  LeaderboardSnapshot,
} from '@/sections/customerManagement/types';
import {
  computeLeaderboardEntries,
  getActiveMetrics,
} from '@/sections/customerManagement/utils/leaderboardTransforms';
import { type QueryStatus, useQuery } from '@/utils/api';

function getYesterdayDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function useLeaderboardSnapshots(): {
  entries: LeaderboardEntry[];
  activeMetrics: ActiveMetric[];
  season: LeaderboardSeason | undefined;
  status: QueryStatus;
} {
  const axios = useAxios({ doNotImpersonate: true });
  const yesterdayDate = getYesterdayDate();

  const todayQuery = useQuery({
    queryKey: ['leaderboard-snapshots'],
    queryFn: async () => {
      const { data } = await axios.get<LeaderboardResponse>('/leaderboard');
      return data;
    },
    defaultErrorMessage: 'Failed to load leaderboard snapshots',
    staleTime: 5 * 60 * 1000,
  });

  const yesterdayQuery = useQuery({
    queryKey: ['leaderboard-snapshots', yesterdayDate],
    queryFn: async () => {
      const { data } = await axios.get<LeaderboardResponse>('/leaderboard', {
        params: { date: yesterdayDate },
      });
      return data;
    },
    defaultErrorMessage: 'Failed to load yesterday snapshots',
    staleTime: 5 * 60 * 1000,
  });

  const todaySnapshots: LeaderboardSnapshot[] =
    todayQuery.data?.snapshots ?? [];
  const yesterdaySnapshots: LeaderboardSnapshot[] =
    yesterdayQuery.data?.snapshots ?? [];

  const entries = useMemo(
    () => computeLeaderboardEntries(todaySnapshots, yesterdaySnapshots),
    [todaySnapshots, yesterdaySnapshots]
  );

  const activeMetrics = useMemo(
    () => getActiveMetrics(todaySnapshots),
    [todaySnapshots]
  );

  const status: QueryStatus =
    todayQuery.status === 'pending' || yesterdayQuery.status === 'pending'
      ? 'pending'
      : todayQuery.status === 'error'
        ? 'error'
        : 'success';

  return { entries, activeMetrics, season: todayQuery.data?.season, status };
}
